import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CreditCard, Landmark, RotateCcw } from 'lucide-react';

const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(n) || 0);

// Per-transaction Payment Details list with a working Refund button (Inspyre/NMI).
export const PaymentTransactions = ({ proposal, onRefunded }) => {
  const [refunding, setRefunding] = useState(null);
  const payments = proposal?.payments || [];
  if (payments.length === 0) return null;

  const refund = async (p) => {
    const amt = Number(p.amount_charged || p.amount || 0);
    if (!window.confirm(`Refund ${fmt(amt)} to the customer's ${p.method === 'ach' ? 'bank account' : 'card'}?\n\nTransaction ${p.transactionid}\nThis processes a real refund through Inspyre.`)) return;
    setRefunding(p.transactionid);
    try {
      await axios.post('/inspyre/refund', {
        transactionid: p.transactionid,
        amount: amt,
        proposal_id: proposal.id,
      });
      toast.success('Refund processed');
      onRefunded?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Refund failed');
    } finally {
      setRefunding(null);
    }
  };

  return (
    <div style={{ marginTop: 12, borderTop: '1px solid #F1F5F9', paddingTop: 10 }} data-testid="payment-transactions">
      {payments.map((p, i) => {
        const isManual = String(p.transactionid || '').startsWith('manual-') || p.gateway !== 'inspyre';
        const refundable = !p.refunded && !isManual && p.transactionid;
        const billing = p.billing || {};
        const billName = [billing.first_name, billing.last_name].filter(Boolean).join(' ');
        return (
          <div key={p.transactionid || i} style={{ background: '#F8FAFC', borderRadius: 6, padding: 10, marginBottom: 8 }} data-testid={`payment-txn-${i}`}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <span style={{ fontSize: 12, color: '#64748B' }}>
                {p.created_at ? new Date(p.created_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
              </span>
              {p.refunded ? (
                <span style={{ fontSize: 10, fontWeight: 700, color: '#DC2626', background: '#FEF2F2', border: '1px solid #FECACA', padding: '1px 6px', borderRadius: 999 }}>REFUNDED</span>
              ) : (
                <span style={{ fontSize: 10, fontWeight: 700, color: '#15803D', background: '#F0FDF4', border: '1px solid #BBF7D0', padding: '1px 6px', borderRadius: 999 }}>PAID</span>
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#0F172A', fontWeight: 600 }}>
                {p.method === 'ach' ? <Landmark size={13} color="#64748B" /> : <CreditCard size={13} color="#64748B" />}
                {p.method === 'ach'
                  ? 'ACH'
                  : `${(p.card_type || 'Card').replace(/\b\w/g, (c) => c.toUpperCase())}${p.card_last4 ? ` - ${p.card_last4}` : ''}`}
              </span>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#0F172A' }}>{fmt(p.amount_charged || p.amount)}</span>
            </div>
            {Number(p.cc_surcharge) > 0 && (
              <div style={{ fontSize: 11, color: '#94A3B8', marginBottom: 2 }}>
                incl. {fmt(p.cc_surcharge)} card surcharge
              </div>
            )}
            <div style={{ fontSize: 11, color: '#64748B', wordBreak: 'break-all' }}>
              Txn: <span style={{ color: '#2563EB' }}>{p.transactionid || '—'}</span>
              {p.authcode && <span style={{ marginLeft: 8 }}>Auth: {p.authcode}</span>}
            </div>
            {(billName || billing.address1) && (
              <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 4, lineHeight: 1.5 }}>
                {billName && <div style={{ textTransform: 'uppercase' }}>{billName}</div>}
                {billing.address1 && <div style={{ textTransform: 'uppercase' }}>{billing.address1}</div>}
                {(billing.city || billing.state) && (
                  <div style={{ textTransform: 'uppercase' }}>{[billing.city, billing.state].filter(Boolean).join(', ')} {billing.postal_code || ''}</div>
                )}
              </div>
            )}
            {refundable && (
              <button
                onClick={() => refund(p)}
                disabled={refunding === p.transactionid}
                data-testid={`refund-txn-btn-${i}`}
                style={{
                  marginTop: 8, width: '100%', background: '#fff', border: '1px solid #FECACA',
                  color: '#DC2626', padding: '6px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700,
                  letterSpacing: '0.04em', textTransform: 'uppercase',
                  cursor: refunding === p.transactionid ? 'wait' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                }}
              >
                <RotateCcw size={12} />
                {refunding === p.transactionid ? 'Refunding…' : 'Refund Transaction'}
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default PaymentTransactions;
